import React, { useRef, useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useSnackbar } from '../context/SnackbarContext';
import './AdminOrders.css';

interface OrderItem {
  productId: string;
  name: string;
  quantity: number;
  price: number;
}

interface ShippingAddress {
  fullName: string;
  phone: string;
  addressLine1: string;
  addressLine2?: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
}

interface PreferredDelivery {
  date: string;
  timeSlot: string;
  instructions?: string;
}

interface Order {
  id: string;
  userId: string;
  userName: string;
  items: OrderItem[];
  total: number;
  deliveryFee?: number;
  deliveryEta?: string;
  deliveryQuoteId?: string;
  fulfillmentType: 'delivery' | 'pickup';
  shippingAddress?: ShippingAddress;
  preferredDelivery?: PreferredDelivery;
  status: 'pending' | 'delivery' | 'completed' | 'cancelled';
  uberOrderId?: string;
  uberStatus?: string;
  uberTrackingUrl?: string;
  uberPickupAt?: string;
  uberDropoffAt?: string;
  createdAt: string;
  updatedAt: string;
}

type OrderCardProps = {
  order: Order;
  expanded: boolean;
  onToggle: (orderId: string) => void;
  onUpdateStatus: (orderId: string, status: string) => void;
  onHail: (orderId: string) => void;
};

const OrderCard: React.FC<OrderCardProps> = React.memo(
  ({ order, expanded, onToggle, onUpdateStatus, onHail }) => (
    <div className="order-card">
      <button
        type="button"
        className="order-header order-toggle"
        onClick={() => onToggle(order.id)}
        aria-expanded={expanded}
      >
        <div>
          <h3>Order #{order.id.slice(0, 8)}</h3>
          <p className="order-customer">Customer: {order.userName}</p>
          <p className="order-date">{new Date(order.createdAt).toLocaleString()}</p>
        </div>
        <div className="order-status-section">
          <div className={`order-status order-status-${order.status}`}>{order.status}</div>
          <select
            value={order.status}
            onChange={(e) => onUpdateStatus(order.id, e.target.value)}
            className="status-select"
            onClick={(e) => e.stopPropagation()}
          >
            <option value="pending">Pending</option>
            <option value="delivery">Delivery</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <span className={`order-chevron ${expanded ? 'open' : ''}`}>▾</span>
        </div>
      </button>

      {expanded && (
        <>
          <div className="order-items">
            <h4>Items:</h4>
            <table className="order-items-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Quantity</th>
                  <th>Price</th>
                  <th>Subtotal</th>
                </tr>
              </thead>
              <tbody>
                {order.items.map((item, index) => (
                  <tr key={index}>
                    <td>{item.name}</td>
                    <td>{item.quantity}</td>
                    <td>${item.price.toFixed(2)}</td>
                    <td>${(item.price * item.quantity).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="order-shipping-details">
            <h4>Fulfillment</h4>
            <div className="shipping-row">
              <div className="shipping-line">
                {order.fulfillmentType === 'delivery' ? 'Delivery' : 'Pickup'}
              </div>
            </div>
            {order.fulfillmentType === 'delivery' && (order.uberStatus || order.uberTrackingUrl) && (
              <div className="shipping-row">
                <div className="shipping-line">
                  <strong>Uber Direct</strong>
                </div>
                {order.uberStatus && <div className="shipping-line">{order.uberStatus}</div>}
                {order.uberPickupAt && (
                  <div className="shipping-line">
                    Picked up: {new Date(order.uberPickupAt).toLocaleString()}
                  </div>
                )}
                {order.uberDropoffAt && (
                  <div className="shipping-line">
                    Delivered: {new Date(order.uberDropoffAt).toLocaleString()}
                  </div>
                )}
                {order.uberTrackingUrl && (
                  <div className="shipping-line">
                    <a href={order.uberTrackingUrl} target="_blank" rel="noreferrer">
                      Track delivery
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>

          {order.fulfillmentType === 'delivery' && order.shippingAddress && (
            <div className="order-shipping-details">
              <h4>Shipping Address</h4>
              <div className="shipping-row">
                <div className="shipping-line">{order.shippingAddress.fullName}</div>
                <div className="shipping-line">{order.shippingAddress.addressLine1}</div>
                {order.shippingAddress.addressLine2 && (
                  <div className="shipping-line">{order.shippingAddress.addressLine2}</div>
                )}
                <div className="shipping-line">
                  {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}
                </div>
                <div className="shipping-line">{order.shippingAddress.country}</div>
                <div className="shipping-line">{order.shippingAddress.phone}</div>
              </div>
            </div>
          )}

          {order.fulfillmentType === 'delivery' && order.preferredDelivery && (
            <div className="order-shipping-details">
              <h4>Preferred Delivery</h4>
              <div className="shipping-row">
                <div className="shipping-line">
                  {order.preferredDelivery.date} — {order.preferredDelivery.timeSlot}
                </div>
                {order.preferredDelivery.instructions && (
                  <div className="shipping-line">{order.preferredDelivery.instructions}</div>
                )}
              </div>
            </div>
          )}

          <div className="order-total">
            <strong>Total: ${order.total.toFixed(2)}</strong>
          </div>

          {order.fulfillmentType === 'delivery' && order.status === 'pending' && (
            <div className="order-actions">
              <button type="button" className="btn btn-secondary" onClick={() => onHail(order.id)}>
                Request Uber Courier
              </button>
            </div>
          )}
        </>
      )}
    </div>
  )
);

const AdminOrders: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const itemsPerPage = 10;
  const { showSuccess, showError } = useSnackbar();
  const initialLoadRef = useRef(true);
  const orderIdsRef = useRef<Set<string>>(new Set());
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set());

  const playPing = () => {
    const AudioContext =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof window.AudioContext }).webkitAudioContext;
    if (!AudioContext) return;
    const context = new AudioContext();
    const oscillator = context.createOscillator();
    const gainNode = context.createGain();

    oscillator.type = 'sine';
    oscillator.frequency.value = 880;
    gainNode.gain.value = 0.08;

    oscillator.connect(gainNode);
    gainNode.connect(context.destination);
    oscillator.start();
    oscillator.stop(context.currentTime + 0.12);
    oscillator.onended = () => context.close();
  };

  const fetchOrders = useCallback(async () => {
    try {
      const response = await api.get('/admin/orders', {
        params: {
          page: currentPage,
          limit: itemsPerPage,
          status: statusFilter,
        },
      });
      const nextOrders: Order[] = response.data.orders || [];
      const nextTotal = Number(response.data.total || 0);
      const nextIds = new Set<string>(nextOrders.map(order => order.id));

      if (!initialLoadRef.current) {
        const hasNewOrder = nextOrders.some((order: Order) => !orderIdsRef.current.has(order.id));
        if (hasNewOrder) {
          playPing();
        }
      }

      orderIdsRef.current = nextIds;
      initialLoadRef.current = false;
      setOrders(nextOrders);
      setTotalCount(nextTotal);
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, statusFilter]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(fetchOrders, 10000);
    return () => clearInterval(interval);
  }, [fetchOrders]);

  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter]);

  const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const updateOrderStatus = useCallback(async (orderId: string, status: string) => {
    try {
      await api.put(`/admin/orders/${orderId}/status`, { status });
      fetchOrders();
      showSuccess('Order status updated successfully');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to update order status');
    }
  }, [fetchOrders, showError, showSuccess]);

  const hailUberCourier = useCallback(async (orderId: string) => {
    try {
      await api.post(`/admin/orders/${orderId}/uber`);
      fetchOrders();
      showSuccess('Uber courier requested');
    } catch (error: any) {
      showError(error.response?.data?.error || 'Failed to request Uber courier');
    }
  }, [fetchOrders, showError, showSuccess]);

  const toggleOrder = useCallback((orderId: string) => {
    setExpandedOrders(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className="container">
        <div className="orders-skeleton">
          {[0, 1, 2].map((item) => (
            <div key={item} className="order-skeleton-card">
              <div className="order-skeleton-line title" />
              <div className="order-skeleton-line meta" />
              <div className="order-skeleton-line meta short" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container">
      <div className="admin-orders-header">
        <h1>Manage Orders</h1>
        <div className="status-filter">
          <label>Filter by Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="filter-select"
          >
            <option value="all">All Orders</option>
            <option value="pending">Pending</option>
            <option value="delivery">Delivery</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="orders-list">
        {orders.length === 0 ? (
          <div className="no-orders">
            <div className="no-orders-icon">📦</div>
            <h2>{statusFilter === 'all' ? 'No orders yet' : 'No orders match this status'}</h2>
            <p>
              {statusFilter === 'all'
                ? 'New orders will appear here as customers checkout.'
                : 'Try a different status to view orders.'}
            </p>
            <div className="no-orders-actions">
              {statusFilter === 'all' ? (
                <button type="button" className="btn btn-secondary" onClick={fetchOrders}>
                  Refresh
                </button>
              ) : (
                <button type="button" className="btn btn-secondary" onClick={() => setStatusFilter('all')}>
                  Clear filter
                </button>
              )}
            </div>
          </div>
        ) : (
          orders.map((order) => (
            <OrderCard
              key={order.id}
              order={order}
              expanded={expandedOrders.has(order.id)}
              onToggle={toggleOrder}
              onUpdateStatus={updateOrderStatus}
              onHail={hailUberCourier}
            />
          ))
        )}
      </div>
      {totalCount > itemsPerPage && (
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
          >
            Previous
          </button>
          <span className="pagination-label">
            Page {currentPage} of {totalPages}
          </span>
          <button
            className="pagination-btn"
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
